"use client"
import React, { useState, useEffect } from "react";
import { Ratio, Alert } from "react-bootstrap";
import UralityImage from "@/components/shared/UralityImage";
import PlaceTypeLabel from "@/components/shared/PlaceTypeLabel";
import MapLink from "@/components/shared/MapLink";
import LocationOpenHoursLink from "@/components/shared/LocationOpenHoursLink";
import ServiceProfileItemSummary from "@/components/locator/ServiceProfileItemSummary";
import { useMediaQuery } from "react-responsive";
import ServiceProfileDetailsModal from "@/components/locator/ServiceProfileDetailsModal";
import CommunityAccoladeBadge from "@/components/communities/CommunityAccoladeBadge";
import ServiceLinkContent from "@/components/locator/ServiceLinkContent";
import { Collapse } from "react-bootstrap";
import SocialLinks from "@/components/social/SocialLinks";

import "@/components/locator/LocatorSearchResultCard.css";
import RichTextBlock from "@/components/shared/RichTextBlock";
import Asset from "@/components/shared/Asset";
import useUtils from "@/components/shared/useUtils";

const LocatorSearchResultCard = ({
  community,
  item,
  searchMode,
  selectedSearchOption
}) => {
  const isSm = useMediaQuery({ query: "(max-width: 767.98px)" });
  const utils = useUtils();

  const [showServiceProfileDetails, setShowServiceProfileDetails] = useState(
    false
  );
  const [showDeliveryDetails, setShowDeliveryDetails] = useState(false);
  const [showPickupDetails, setShowPickupDetails] = useState(false);

  const hoursLink = content => {
    if (content.location && content.location.hours) {
      return (
        <LocationOpenHoursLink location={content.location} showIcon={true} />
      );
    } else {
      return <></>;
    }
  };

  const phoneLink = content => {
    if (content.location) {
      if (content.location.phoneNumber) {
        return (
          <a href={"tel:" + content.location.phoneNumber}>
            <div className="with-icon">
              <i className="fa-solid fa-phone"></i>
              <span>{content.location.phoneNumber}</span>
            </div>
          </a>
        );
      } else {
        return <></>;
      }
    } else {
      return <></>;
    }
  };

  const websiteLink = content => {
    if (content.location) {
      if (content.location.website) {
        return (
          <a
            className="with-icon"
            href={content.location.website}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="with-icon">
              <i className="fa-solid fa-globe"></i>
              <span>{content.location.website}</span>
            </div>
          </a>
        );
      } else {
        return <></>;
      }
    } else {
      return <></>;
    }
  };

  const accolades = content => {
    if (
      content.communityAccoladeIds &&
      content.communityAccoladeIds.length > 0
    ) {
      return (
        <div className="accolade-badge-list">
          {content.communityAccoladeIds.map(function(accoladeId, index) {
            const accolade = community.accolades.find(
              accolade => accolade.id === accoladeId
            );
            if (accolade) {
              return (
                <CommunityAccoladeBadge
                  accolade={accolade}
                  enableHover={true}
                  small={true}
                />
              );
            } else {
              return <></>;
            }
          })}
        </div>
      );
    }
  };

  // a pin layer point (see pinLayer.js) rather than a business
  if (item?.pinLayerPoint) {
    const point = item.pinLayerPoint;
    const customFields = utils.buildCustomFields(point);
    return (
      <div className="locator-search-result-card">
        {point.assets?.length > 0 ? (
          <Asset assets={point.assets} />
        ) : (
          <Ratio aspectRatio={"16x9"}>
            <UralityImage size="md" alt={item.displayName} />
          </Ratio>
        )}
        <div className="name-and-type">
          <h4>{item.displayName}</h4>
          <span>{item.pinLayerTitle}</span>
        </div>
        <div className="locator-search-result-card-content">
          <div className="section">
            <MapLink location={item.location} clickable={true} showNotes={true} />
          </div>
          {point.callToAction && (
            <div className="section">
              <a
                className="btn btn-primary"
                href={point.callToAction.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {point.callToAction.label}
              </a>
            </div>
          )}
        </div>
        {(point.description || customFields) && (
          <div className="locator-search-result-card-content">
            {point.description && <div className="section">{point.description}</div>}
            {customFields && <div className="section">{customFields}</div>}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {showServiceProfileDetails && (
        <ServiceProfileDetailsModal
          profile={item}
          onClose={() => setShowServiceProfileDetails(false)}
        />
      )}
      <div className="locator-search-result-card">
        {item && (
          <>
            <Ratio aspectRatio={"16x9"}>
              <UralityImage
                imageId={item.coverPhotoId}
                size="md"
                alt={item.displayName}
              />
            </Ratio>
            <div className="name-and-type">
              <div className="tags">{accolades(item)}</div>
              <h4>{item.displayName}</h4>
              {searchMode === "business" && (
                <span>
                  <PlaceTypeLabel values={item.types} />
                </span>
              )}
            </div>
            {searchMode === "service" && (
              <div className="locator-search-result-card-content">
                <div className="section">
                  <div
                    className="default-flex-row-layout"
                    style={{ marginBottom: "0.25rem" }}
                  >
                    <h5>Services Offered</h5>
                  </div>

                  <ServiceProfileItemSummary
                    item={item}
                    selectedSearchOption={selectedSearchOption}
                    condensed={true}
                    showNotes={true}
                  />
                  <button
                    style={{ marginTop: "0.5rem" }}
                    className="btn btn-secondary"
                    onClick={() => setShowServiceProfileDetails(true)}
                  >
                    View All Services, Items, and Guidelines
                  </button>
                </div>
                {item.serviceProfile.delivery && (
                  <div className="section">
                    <fieldset>
                      <div className="default-flex-row-layout">
                        <div
                          className="with-icon"
                          style={{ marginBottom: "0.25rem" }}
                        >
                          <i className="fa-solid fa-person-carry-box"></i>
                          <span>
                            <strong>
                              {item.serviceProfile.delivery.free
                                ? "Free Delivery Available"
                                : "Delivery Available"}
                            </strong>
                          </span>
                        </div>
                        <div className="flex-grow" />
                        <button
                          className="a"
                          onClick={() =>
                            setShowDeliveryDetails(!showDeliveryDetails)
                          }
                        >
                          <span>{showDeliveryDetails ? "Less" : "More"}</span>
                        </button>
                      </div>
                      <Collapse in={showDeliveryDetails}>
                        <div>
                          {(item.serviceProfile.delivery.text ||
                            item.serviceProfile.delivery.url ||
                            item.serviceProfile.delivery.emailAddress ||
                            item.serviceProfile.delivery.phoneNumber) && (
                            <>
                              <ServiceLinkContent
                                content={item.serviceProfile.delivery}
                              />
                            </>
                          )}
                        </div>
                      </Collapse>
                    </fieldset>
                  </div>
                )}
                {item.serviceProfile.pickup && (
                  <div className="section">
                    <fieldset>
                      <div className="default-flex-row-layout">
                        <div
                          className="with-icon"
                          style={{ marginBottom: "0.25rem" }}
                        >
                          <i className="fa-solid fa-truck-pickup"></i>
                          <span>
                            <strong>
                              {item.serviceProfile.pickup.free
                                ? "Free Pickup Available"
                                : "Pickup Available"}
                            </strong>
                          </span>
                        </div>
                        <div className="flex-grow" />
                        <button
                          className="a"
                          onClick={() =>
                            setShowPickupDetails(!showPickupDetails)
                          }
                        >
                          <span>{showPickupDetails ? "Less" : "More"}</span>
                        </button>
                      </div>
                      <Collapse in={showPickupDetails}>
                        <div>
                          {(item.serviceProfile.pickup.text ||
                            item.serviceProfile.pickup.url ||
                            item.serviceProfile.pickup.emailAddress ||
                            item.serviceProfile.pickup.phoneNumber) && (
                            <>
                              <ServiceLinkContent
                                content={item.serviceProfile.pickup}
                              />
                            </>
                          )}
                        </div>
                      </Collapse>
                    </fieldset>
                  </div>
                )}
              </div>
            )}
            <div className="locator-search-result-card-content">
              {item.location ? (
                <div className="section">
                  <MapLink location={item.location} useIcon="fa-store" />
                </div>
              ) : (
                <div className="section"></div>
              )}

              {item.location && item.location.hours && (
                <div className="section">{hoursLink(item)}</div>
              )}

              {item.location?.website && (
                <div className="section">{websiteLink(item)}</div>
              )}

              {item.location?.phoneNumber && (
                <div className="section">{phoneLink(item)}</div>
              )}

              {item.publicEmailAddress && (
                <div className="section">
                  <a href={`mailto:${item.publicEmailAddress}`} className="with-icon">
                    <span className="material-icons">mail</span>
                    {item.publicEmailAddress}
                  </a>
                </div>
              )}

              {item.socialProfile && item.socialProfile.length > 1 && (
                <div className="section">
                  <SocialLinks
                    omitWebsite={true}
                    socialProfile={item.socialProfile}
                  />
                </div>
              )}
            </div>
            {item.descriptionRichText && (
              <div className="locator-search-result-card-content">
                <div className="section">
                  <RichTextBlock
                    richText={item.descriptionRichText}
                    collapsable={true}
                    as="span"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
export default LocatorSearchResultCard;
